import React from 'react'

// Darken a hex color by a percentage (negative = darker)
const darkenColor = (hex, percent) => {
  const num = parseInt(hex.replace('#', ''), 16)
  const amt = Math.round(2.55 * percent)
  const R = Math.max(0, Math.min(255, (num >> 16) + amt))
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + amt))
  const B = Math.max(0, Math.min(255, (num & 0x0000ff) + amt))
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`
}

/**
 * Shared branded side panel for auth pages (login / registration).
 * Gradient background in the user-type color, decorative circles and paw
 * prints, a tagline, and a gently floating image (mascot or scene).
 *
 * Pass a `className` so the parent page can hide/size the panel responsively
 * (e.g. hide it on mobile via its own media queries).
 */
const AuthBrandPanel = ({
  color = '#8548A8',
  hoverColor,
  title,
  image,
  imageAlt = '',
  // 'mascot' = transparent PNG (objectFit contain, no frame)
  // 'scene'  = photo (objectFit cover, rounded frame)
  imageShape = 'scene',
  isRTL = false,
  className = '',
  style = {},
}) => {
  const endColor = hoverColor || darkenColor(color, -15)

  return (
    <div
      className={className}
      style={{
        width: '100%',
        height: '100%',
        background: `linear-gradient(155deg, ${color} 0%, ${endColor} 100%)`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '100px 48px 60px 48px',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
    >
      {/* Decorative circles */}
      <div style={{
        position: 'absolute',
        top: '-140px',
        left: isRTL ? 'auto' : '-140px',
        right: isRTL ? '-140px' : 'auto',
        width: '380px',
        height: '380px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.10)',
        zIndex: 1
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-160px',
        right: isRTL ? 'auto' : '-120px',
        left: isRTL ? '-120px' : 'auto',
        width: '420px',
        height: '420px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.08)',
        zIndex: 1
      }} />

      {/* Scattered paw prints */}
      {[
        { top: '10%', left: '8%', size: 46, rotate: -24, opacity: 0.14 },
        { top: '16%', right: '10%', size: 30, rotate: 18, opacity: 0.10 },
        { bottom: '12%', left: '12%', size: 34, rotate: 12, opacity: 0.12 },
        { bottom: '20%', right: '7%', size: 50, rotate: -14, opacity: 0.10 },
        { top: '45%', left: '3%', size: 26, rotate: 30, opacity: 0.10 },
      ].map((paw, i) => (
        <img
          key={i}
          src="/paw-white.svg"
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: paw.top,
            bottom: paw.bottom,
            left: paw.left,
            right: paw.right,
            width: `${paw.size}px`,
            height: `${paw.size}px`,
            opacity: paw.opacity,
            transform: `rotate(${paw.rotate}deg)`,
            zIndex: 1,
            pointerEvents: 'none',
            userSelect: 'none'
          }}
        />
      ))}

      {/* Composed content group: tagline + image */}
      <div className="auth-brand-panel-content" style={{
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        width: '100%'
      }}>
        {title && (
          <>
            <h2 style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(22px, 2.4vw, 32px)',
              lineHeight: '1.25',
              color: '#FFFFFF',
              margin: 0,
              letterSpacing: '-0.02em',
              textAlign: 'center',
              textShadow: '0 2px 12px rgba(0, 0, 0, 0.18)'
            }}>
              {title}
            </h2>
            <div style={{
              width: '56px',
              height: '4px',
              borderRadius: '2px',
              background: 'rgba(255, 255, 255, 0.65)',
              marginBottom: '8px'
            }} />
          </>
        )}

        {/* Floating image */}
        {image && (
          <div style={{
            position: 'relative',
            width: 'min(460px, 85%)',
            aspectRatio: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'authBrandFloat 8s ease-in-out infinite'
          }}>
            {/* Soft glow behind the image */}
            <div style={{
              position: 'absolute',
              inset: '14%',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.22)',
              filter: 'blur(48px)',
              zIndex: 1
            }} />
            <img
              src={image}
              alt={imageAlt}
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                objectFit: imageShape === 'scene' ? 'cover' : 'contain',
                borderRadius: imageShape === 'scene' ? '24px' : '0',
                boxShadow: imageShape === 'scene'
                  ? '0 20px 44px rgba(0, 0, 0, 0.30)'
                  : 'none',
                filter: imageShape === 'mascot'
                  ? 'drop-shadow(0 20px 36px rgba(0, 0, 0, 0.28))'
                  : 'none',
                zIndex: 2
              }}
              onError={(e) => {
                e.target.style.display = 'none'
              }}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes authBrandFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-16px); }
        }
      `}</style>
    </div>
  )
}

export default AuthBrandPanel
